import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { MotionConfig } from 'framer-motion';
import { AuthProvider } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Programs from './pages/Programs';
import Admissions from './pages/Admissions';
import Contact from './pages/Contact';
import Gallery from './pages/Gallery';
import Privacy from './pages/Privacy';
import TrackApplication from './pages/TrackApplication';
import Faq from './pages/Faq';
import Facilities from './pages/Facilities';
import NotFound from './pages/NotFound';
import AdminLogin from './pages/AdminLogin';
import AdminResetPassword from './pages/AdminResetPassword';
import AdminAuditLog from './pages/AdminAuditLog';
import AdminGallery from './pages/AdminGallery';
import AdminGalleryAlbum from './pages/AdminGalleryAlbum';
import AdminDashboard from './pages/AdminDashboard';
import ApplicationsManagement from './pages/ApplicationsManagement';
import AdminContacts from './pages/AdminContacts';
import UserManagement from './pages/UserManagement';
import AdmissionControls from './pages/AdmissionControls';
import AdminSecurity from './pages/AdminSecurity';
import AdminSchoolSettings from './pages/AdminSchoolSettings';
import AdminNotifications from './pages/AdminNotifications';
import ApplicationPrint from './pages/ApplicationPrint';
import { SchoolProvider } from './contexts/SchoolContext';
import { captureAttribution } from './lib/attribution';
import { initAnalytics, trackPageView } from './lib/analytics';
import ProtectedRoute from './components/ProtectedRoute';
import Seo from './seo/Seo';
import './App.css';

// Remember where this visit came from (utm parameters / external referrer) before any navigation happens.
captureAttribution();
initAnalytics();

const ScrollToTop = () => {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    // Admin pages are not tracked.
    if (!location.pathname.startsWith('/admin')) trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

// Public pages: per-page head tags (from seo/pages.json), a skip link, header, main content and footer.
// Pages that add structured data (Home, FAQ) render their own <Seo> as well; the later one wins.
export const PublicPage = ({ path, children, noindex = false }) => (
  <>
    <Seo path={path} noindex={noindex} />
    <a href="#main-content" className="skip-link">Skip to main content</a>
    <Header />
    <main id="main-content" className="main-content" tabIndex={-1}>
      {children}
    </main>
    <Footer />
  </>
);

// Admin pages are never indexed.
const Admin = ({ children, title = 'Admin Portal | KSMS' }) => (
  <>
    <Seo title={title} noindex />
    {children}
  </>
);

const PUBLIC_ROUTES = [
  ['/', Home], ['/about', About], ['/programs', Programs], ['/admissions', Admissions], ['/gallery', Gallery],
  ['/contact', Contact], ['/faq', Faq], ['/facilities', Facilities], ['/track', TrackApplication], ['/privacy', Privacy]
];

const STAFF_ROUTES = [
  ['/admin/dashboard', AdminDashboard],
  ['/admin/applications', ApplicationsManagement],
  ['/admin/applications/:id', ApplicationsManagement],
  ['/admin/applications/:id/print', ApplicationPrint],
  ['/admin/contacts', AdminContacts],
  ['/admin/admission-controls', AdmissionControls],
  ['/admin/school-settings', AdminSchoolSettings],
  ['/admin/gallery', AdminGallery],
  ['/admin/gallery/:id', AdminGalleryAlbum],
  ['/admin/security', AdminSecurity],
  ['/admin/users', UserManagement, 'super-admin'],
  ['/admin/notifications', AdminNotifications, 'super-admin'],
  ['/admin/audit', AdminAuditLog, 'super-admin']
];

function App() {
  return (
    <HelmetProvider>
      {/* Visitors who ask their system for reduced motion get no entrance animations. */}
      <MotionConfig reducedMotion="user">
        <AuthProvider>
          <SchoolProvider>
            <Router>
              <div className="App">
                <ScrollToTop />
                <Routes>
                  {PUBLIC_ROUTES.map(([path, Page]) => (
                    <Route key={path} path={path} element={<PublicPage path={path}><Page /></PublicPage>} />
                  ))}

                  {/* Admin routes (no public header/footer) */}
                  <Route path="/admin/login" element={<Admin title="Sign in | KSMS Admin"><AdminLogin /></Admin>} />
                  <Route
                    path="/admin/reset-password"
                    element={<Admin title="Choose a new password | KSMS Admin"><AdminResetPassword /></Admin>}
                  />
                  {STAFF_ROUTES.map(([path, Page, role]) => (
                    <Route
                      key={path}
                      path={path}
                      element={<Admin><ProtectedRoute requireRole={role}><Page /></ProtectedRoute></Admin>}
                    />
                  ))}

                  <Route path="*" element={<PublicPage noindex><NotFound /></PublicPage>} />
                </Routes>
              </div>
            </Router>
          </SchoolProvider>
        </AuthProvider>
      </MotionConfig>
    </HelmetProvider>
  );
}

export default App;
